import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Command, CommandInput, CommandList, CommandGroup, CommandItem } from "@/components/ui/command";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { User, PawPrint, FileText, CreditCard, CalendarDays, Scissors, Package, BadgeCheck, Users, ArrowRight, Loader2 } from "lucide-react";

// Each category: label, icon, how to render a row, and where selecting it navigates.
const CATS = [
  { key: "customers", label: "Customers", icon: User,
    primary: (r) => r.name, secondary: (r) => r.mobile || "", nav: (r) => `/customers/${r.id}` },
  { key: "pets", label: "Pets", icon: PawPrint,
    primary: (r) => r.name, secondary: (r) => r.breed || "", nav: (r) => `/pets/${r.id}` },
  { key: "bookings", label: "Bookings", icon: CalendarDays,
    primary: (r) => r.customer_name || "Booking", secondary: (r) => [r.service_name, r.pet_name].filter(Boolean).join(" · "),
    nav: (r) => `/bookings?search=${encodeURIComponent(r.customer_name || "")}` },
  { key: "invoices", label: "Invoices", icon: FileText,
    primary: (r) => r.invoice_number, secondary: (r) => `${r.customer_name || ""} · ${inr(r.total)}`,
    nav: (r) => `/invoices?search=${encodeURIComponent(r.invoice_number || "")}` },
  { key: "payments", label: "Payments", icon: CreditCard,
    primary: (r) => `${r.invoice_number || "Payment"} · ${inr(r.amount)}`, secondary: (r) => `${r.customer_name || ""} · ${(r.method || "").toUpperCase()}`,
    nav: (r) => `/payments?search=${encodeURIComponent(r.invoice_number || r.customer_name || "")}` },
  { key: "memberships", label: "Memberships", icon: BadgeCheck,
    primary: (r) => r.plan_name || "Membership", secondary: (r) => r.customer_name || "",
    nav: (r) => `/memberships?search=${encodeURIComponent(r.customer_name || r.plan_name || "")}` },
  { key: "services", label: "Services", icon: Scissors,
    primary: (r) => r.name, secondary: (r) => [r.category, r.price != null ? inr(r.price) : ""].filter(Boolean).join(" · "),
    nav: (r) => `/services?search=${encodeURIComponent(r.name || "")}` },
  { key: "products", label: "Inventory", icon: Package,
    primary: (r) => r.name, secondary: (r) => [r.sku, r.stock != null ? `${r.stock} in stock` : ""].filter(Boolean).join(" · "),
    nav: (r) => `/inventory?search=${encodeURIComponent(r.name || "")}` },
  { key: "staff", label: "Staff", icon: Users,
    primary: (r) => r.name, secondary: (r) => r.role || "", nav: (r) => `/staff?search=${encodeURIComponent(r.name || "")}` },
];

const MODULE_LIST = {
  bookings: "/bookings", invoices: "/invoices", payments: "/payments",
  memberships: "/memberships", services: "/services", products: "/inventory", staff: "/staff",
};

export function GlobalSearch({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 250); return () => clearTimeout(t); }, [q]);

  useEffect(() => {
    if (!open) return;
    if (dq.length < 2) { setData(null); setError(false); setLoading(false); return; }
    let alive = true;
    setLoading(true); setError(false);
    api.get(`/search?q=${encodeURIComponent(dq)}`)
      .then(({ data }) => { if (alive) { setData(data); setLoading(false); } })
      .catch(() => { if (alive) { setError(true); setLoading(false); } });
    return () => { alive = false; };
  }, [dq, open]);

  useEffect(() => { if (!open) { setQ(""); setDq(""); setData(null); setError(false); } }, [open]);

  const go = (path) => { onOpenChange(false); navigate(path); };

  const activeCats = CATS.filter((c) => data && Array.isArray(data[c.key]) && data[c.key].length > 0);
  const hasAny = activeCats.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 gap-0 max-w-xl top-[12%] translate-y-0" data-testid="global-search-dialog" aria-describedby="global-search-desc">
        <DialogTitle className="sr-only">Global search</DialogTitle>
        <DialogDescription id="global-search-desc" className="sr-only">Search customers, pets, bookings, invoices, payments and more.</DialogDescription>
        <Command shouldFilter={false} className="rounded-lg">
          <CommandInput
            value={q} onValueChange={setQ} autoFocus
            placeholder="Search customers, pets, bookings, invoices, payments…"
            data-testid="global-search-command-input" />
          <CommandList className="max-h-[60vh]">
            {dq.length >= 2 && loading && (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500" data-testid="global-search-loading">
                <Loader2 className="h-4 w-4 animate-spin" /> Searching…
              </div>
            )}
            {dq.length >= 2 && !loading && error && (
              <div className="py-8 text-center text-sm text-rose-600" data-testid="global-search-error">
                Something went wrong. Please try again.
              </div>
            )}
            {dq.length >= 2 && !loading && !error && !hasAny && (
              <div className="py-8 text-center text-sm text-slate-400" data-testid="global-search-empty">
                No matching records found.
              </div>
            )}
            {dq.length < 2 && (
              <div className="py-8 text-center text-sm text-slate-400" data-testid="global-search-hint">
                Type at least 2 characters. Use <kbd className="rounded border px-1">/</kbd> or <kbd className="rounded border px-1">⌘K</kbd> to open.
              </div>
            )}
            {!loading && !error && activeCats.map((c) => (
              <CommandGroup key={c.key} heading={c.label}>
                {data[c.key].map((r) => {
                  const Icon = c.icon;
                  return (
                    <CommandItem key={`${c.key}-${r.id}`} value={`${c.key}-${r.id}`} onSelect={() => go(c.nav(r))}
                      data-testid={`global-search-result-${c.key}-${r.id}`} className="cursor-pointer">
                      <Icon className="text-slate-400" />
                      <span className="truncate text-slate-800">{c.primary(r)}</span>
                      {c.secondary(r) && <span className="ml-auto truncate text-xs text-slate-400 pl-3">{c.secondary(r)}</span>}
                    </CommandItem>
                  );
                })}
                {MODULE_LIST[c.key] && data[c.key].length >= 6 && (
                  <CommandItem value={`viewall-${c.key}`} onSelect={() => go(`${MODULE_LIST[c.key]}?search=${encodeURIComponent(dq)}`)}
                    data-testid={`global-search-viewall-${c.key}`} className="cursor-pointer text-primary">
                    <ArrowRight /> View all {c.label.toLowerCase()} matching “{dq}”
                  </CommandItem>
                )}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
